import { useEffect, useState } from 'react';
import { Icon } from '../results/Icon.js';
import type { LocationState } from './location-service.js';

/**
 * Location control. Manual place entry is the primary path. GPS runs only when
 * the visitor presses "Use my location" — this component never calls
 * geolocation itself. The parent owns that call, and only from this button.
 *
 * Until a place is chosen, copy states that results are measured from Seattle
 * Center. A chosen location can be reset back to that default.
 */
export function LocationPrompt({
  state,
  onUseGps,
  onManualSubmit,
  onClear,
}: {
  state: LocationState;
  onUseGps: () => void;
  onManualSubmit: (placeText: string) => void;
  onClear: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const busy = state.status === 'requesting' || state.status === 'resolving';
  const chosen = state.source !== 'default';
  const [open, setOpen] = useState(state.status === 'error');

  useEffect(() => {
    if (state.status === 'error' || busy) {
      setOpen(true);
      return;
    }
    // A successful search or GPS fix closes the form, including the privacy note.
    if (state.status === 'ready' && state.source !== 'default') setOpen(false);
  }, [state.status, state.source, busy]);

  return (
    <section className="location-prompt" aria-labelledby="location-heading" aria-busy={busy}>
      <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
        <summary id="location-heading" className="location-heading">
          {heading(state)}
        </summary>
        <p className="location-detail">{detail(state)}</p>

        <div className="location-controls">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) onManualSubmit(text.trim());
            }}
          >
            <label htmlFor="place">Neighborhood, ZIP, or address</label>
            <input
              id="place"
              name="place"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Capitol Hill or 98101"
              autoComplete="off"
              maxLength={300}
            />
            <button type="submit" className="btn">
              Find events
            </button>
          </form>
          <div className="location-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onUseGps}
              aria-describedby="location-privacy"
            >
              Use my location
            </button>
            {chosen ? (
              <button type="button" className="btn btn-ghost" onClick={onClear}>
                Reset to Seattle Center
              </button>
            ) : null}
          </div>
        </div>

        {state.status === 'error' ? (
          <p role="alert" className="location-error">
            {errorMessage(state)}
          </p>
        ) : null}

        <p className="privacy-note" id="location-privacy">
          Your location stays in this browser session only (cleared within 24 hours) and is never
          stored to a profile or shared. We don't ask for GPS until you choose "Use my location".
        </p>
        <ul className="trust-badges" aria-label="why you can trust this">
          <li className="trust-badge">
            <Icon name="check" size={14} strokeWidth={3} /> No sign-up
          </li>
          <li className="trust-badge">
            <Icon name="shield" size={14} /> Your location stays on your device
          </li>
          <li className="trust-badge">
            <Icon name="ticket" size={14} /> Links to the official page
          </li>
        </ul>
      </details>
    </section>
  );
}

function heading(state: LocationState): string {
  if (state.status === 'requesting') return 'Asking your browser for location…';
  if (state.status === 'resolving') return 'Looking up that place…';
  if (state.source === 'gps') return 'Near you';
  if (state.source === 'manual' && state.label) return `Near ${state.label}`;
  return 'Near Seattle Center';
}

function detail(state: LocationState): string {
  if (state.status === 'requesting' || state.status === 'resolving') {
    return 'The list stays up until this finishes.';
  }
  if (state.source === 'default') {
    return 'Enter a neighborhood, or use your location when you want distances from where you are. GPS stays off until you ask.';
  }
  if (state.source === 'gps') {
    return 'Distances are measured from where you are. Reset to go back to Seattle Center.';
  }
  return 'Reset to go back to Seattle Center.';
}

function errorMessage(state: Extract<LocationState, { status: 'error' }>): string {
  if (state.reason === 'unresolved') {
    return "Couldn't find that place — try a neighborhood or ZIP. Distances stay where they were.";
  }
  if (state.reason === 'denied') {
    return state.source === 'default'
      ? 'Location permission was declined. Still showing events near Seattle Center — enter a place, or try again.'
      : 'Location permission was declined. Distances stay where they were — enter a place, or try again.';
  }
  return state.source === 'default'
    ? 'Location is unavailable. Still showing events near Seattle Center — enter a place instead.'
    : 'Location is unavailable. Distances stay where they were — enter a place instead.';
}
